import { Component, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastController } from '@ionic/angular';
import {
  Category,
  Transaction,
  TransactionFirebaseService,
  UtilsFirebaseService,
} from 'app/shared';
import { MessagesEnum } from 'app/shared/data/enums/messages.fr.enum';

@Component({
    selector: 'app-transaction-modal',
    templateUrl: './transaction-modal.component.html',
    styleUrls: ['./transaction-modal.component.scss'],
    standalone: false
})
export class TransactionModalComponent implements OnInit {
  // Form utilities
  public addTransactionForm: FormGroup;
  public serverError: string;
  public messages = MessagesEnum;

  // Core props - ionic makes the link automatically for the value
  public addedTransaction$: EventEmitter<Transaction>;
  public alteredTransaction$: EventEmitter<Array<Transaction>>;
  public transactionToAlter: Transaction;
  public isExpense: boolean = null;
  public providedDate: Date = null;

  // Form props
  public todayDate: Date = new Date();
  public todayDateString: string = this.todayDate.toISOString();
  public currencies: Array<string> = [];
  public categories: Array<Category> = [];

  // User
  public readonly defaultSelectedCurrency = 'EUR'; // TODO : assign default to user
  constructor(
    private formBuilder: FormBuilder,
    private utilsFirebaseService: UtilsFirebaseService,
    private transactionService: TransactionFirebaseService,
    private toastController: ToastController
  ) {}

  async ngOnInit() {
    // Get the available lists from firebase
    this.currencies = await this.utilsFirebaseService.getAvailableLanguages();
    this.categories = await this.utilsFirebaseService.getDefaultCategories();

    this.buildForm();
  }

  public buildForm() {
    if (!this.transactionToAlter) {
      // Create the form with initial values
      this.addTransactionForm = this.formBuilder.group({
        amount: [null, Validators.required],
        label: [null, Validators.required],
        category: [null, Validators.required],
        currency: [this.defaultSelectedCurrency, Validators.required],
        date: [this.computeDateProperty(), Validators.required],
      });
    } else {
      // Create the form with provided values
      const t = this.transactionToAlter;
      this.isExpense = t.amount < 0;
      this.addTransactionForm = this.formBuilder.group({
        amount: [t.amount, Validators.required],
        label: [t.label, Validators.required],
        category: [t.idCategory, Validators.required],
        currency: [t.currency, Validators.required],
        date: [t.id.split('T')[0], Validators.required],
      });
    }
  }

  public async close() {
    const modalElement = document.querySelector('ion-modal');
    await modalElement?.dismiss();
  }

  /**
   * Constructs transaction and call service
   */
  public async addTransaction() {
    // Get values from form
    const amount: number = this.addTransactionForm.get('amount').value;
    const label = this.addTransactionForm.get('label').value;
    const categoryId = this.addTransactionForm.get('category').value;
    const currency = this.addTransactionForm.get('currency').value;

    // Date logic (add the current time to prevent random sorting on the same date)
    const isoStringDate: string =
      this.addTransactionForm.get('date').value +
      'T' +
      this.todayDateString.split('T')[1];

    // If there is a transaction provided, this is an alter
    if (this.transactionToAlter) {
      this.isExpense = amount < 0;
    }

    // The transaction
    const transaction: Transaction = {
      id: isoStringDate,
      idCategory: +categoryId,
      amount: this.transactionToAlter
        ? amount
        : this.isExpense
        ? amount * -1
        : amount,
      label,
      currency,
    };

    try {
      const updatedTransaction = await this.transactionService.saveTransaction(
        transaction,
        this.transactionToAlter
      );

      // French tenses and so on construction
      let msgPrefix: string;
      let msgSuffix: string;

      if (this.transactionToAlter ? amount < 0 : this.isExpense) {
        msgPrefix = 'La dépense de';
        msgSuffix = `a bien été enregistrée.`;
      } else {
        msgPrefix = 'Le revenu de';
        msgSuffix = `a bien été enregistré.`;
      }
      this.displayToast(
        `${msgPrefix} ${updatedTransaction.amount} ${currency} ${msgSuffix}`,
        'success'
      );

      // Handle alter or add case differently
      if (this.transactionToAlter) {
        this.alteredTransaction$.emit([
          updatedTransaction,
          this.transactionToAlter,
        ]);
      } else {
        this.addedTransaction$.emit(updatedTransaction);
      }
      this.close();
    } catch (err) {
      this.displayToast(
        `Une erreur s'est produite lors de l'enregistrement de la transaction. Veuillez réessayer plus tard.`,
        'danger'
      );
    }
  }

  /**
   * Compute the provided dateTime and convert it to a Date
   */
  private computeDateProperty = () => {
    if (this.providedDate.getMonth() === this.todayDate.getMonth()) {
      return this.todayDate.toISOString().split('T')[0];
    } else {
      // Othewise, set the date of the month to the first day
      this.providedDate.setDate(1);
      return this.providedDate.toISOString().split('T')[0];
    }
  };

  /**
   * Function to call with message and type in order to display a toaster
   *
   * @param message The message to disoplay
   * @param color The type
   */
  private displayToast = async (
    message: string,
    color: 'danger' | 'success'
  ) => {
    const toast = await this.toastController.create({
      color,
      duration: 2000,
      message,
    });

    await toast.present();
  };
}
